import { Badge, BadgeProps, Loader, Tooltip } from '@mantine/core';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { getFaultKindBadgeColor, getFaultKindPretty } from 'components/tables/data/fault/kind';
import { FaultRow } from 'components/tables/data/fault/types';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DEVICE_FAULTS_PAGE_PATH, FORM_PAGE_PATH, REVISION_PAGE_PATH } from 'routes/paths';

const WRAPPABLE_BADGE_STYLES: BadgeProps['styles'] = {
  root: {
    height: 'auto',
    maxWidth: '100%',
    minWidth: 0,
    overflow: 'visible',
    whiteSpace: 'normal',
  },
  inner: {
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
};

/**
 * Column for the fault kind/source.
 */
export default function FaultKindColumn({ data: { kind, revision, deviceId } }: { data: FaultRow }) {
  const { getAction, roleId } = useApi();
  const navigate = useNavigate();
  const [isCheckingPermission, setIsCheckingPermission] = useState(false);
  const [hasRevisionAccess, setHasRevisionAccess] = useState<boolean | undefined>(undefined);
  const revisionKind = revision?.kind ?? kind;
  const revisionDisplayId = revision ? `#${revision.revisionId}` : undefined;

  const badgeLabel =
    revisionDisplayId && revisionKind !== 'manual'
      ? `${getFaultKindPretty(kind)} ${revisionDisplayId}`
      : getFaultKindPretty(kind);

  const tooltipLabel =
    revision && revisionKind !== 'manual' ? (
      hasRevisionAccess === false ? (
        <>
          {revision.revisionName && <span style={{ display: 'block' }}>{revision.revisionName}</span>}
          <span style={{ display: 'block' }}>Nemáte oprávnenie zobraziť tento záznam.</span>
        </>
      ) : (
        revision.revisionName
      )
    ) : undefined;

  const { path, targetType } = useMemo(() => {
    if (revisionKind === 'manual') {
      return {
        path: DEVICE_FAULTS_PAGE_PATH.insert({ deviceId }),
        targetType: 'device' as const,
      };
    }

    if (!revision) {
      return {
        path: undefined,
        targetType: undefined,
      };
    }

    if (revisionKind === 'revision') {
      return {
        path: REVISION_PAGE_PATH.insert({ revisionId: revision.revisionId }),
        targetType: 'revision' as const,
      };
    }

    return {
      path: FORM_PAGE_PATH.insert({ revisionId: revision.revisionId }),
      targetType: 'form' as const,
    };
  }, [deviceId, revisionKind, revision]);

  const isDisabled = isCheckingPermission || hasRevisionAccess === false;

  const badge = (
    <Badge color={getFaultKindBadgeColor(kind)} size="sm" styles={WRAPPABLE_BADGE_STYLES} variant="filled">
      {badgeLabel}
    </Badge>
  );

  const badgeWithTooltip = tooltipLabel ? (
    <Tooltip label={tooltipLabel} openDelay={200} withinPortal>
      <span style={{ display: 'inline-flex', maxWidth: '100%', minWidth: 0 }}>{badge}</span>
    </Tooltip>
  ) : (
    badge
  );

  /**
   * Checks revision visibility lazily and navigates only when the current row is accessible.
   */
  async function handleClick() {
    if (!path || !targetType || isDisabled) {
      return;
    }

    if (roleId === ROLE_ADMIN_ID || targetType === 'device') {
      navigate(path);

      return;
    }

    if (hasRevisionAccess === true) {
      navigate(path);

      return;
    }

    setIsCheckingPermission(true);

    try {
      const listRevisions = getAction('RevisionList');
      const revisions = await listRevisions({ query: { filters: { 'revisionId.eq': revision!.revisionId } } });
      const hasAccess = revisions.length > 0;

      setHasRevisionAccess(hasAccess);

      if (hasAccess) {
        navigate(path);
      }
    } catch (error) {
      panic(error as Error);
    } finally {
      setIsCheckingPermission(false);
    }
  }

  if (!path) {
    return badgeWithTooltip;
  }

  return (
    <button
      aria-disabled={isDisabled}
      onClick={handleClick}
      style={{
        alignItems: 'center',
        background: 'none',
        border: 'none',
        cursor: isCheckingPermission ? 'progress' : hasRevisionAccess === false ? 'not-allowed' : 'pointer',
        display: 'inline-flex',
        gap: 6,
        maxWidth: '100%',
        minWidth: 0,
        padding: 0,
      }}
      type="button"
    >
      {badgeWithTooltip}
      {isCheckingPermission && <Loader color="gray.7" size="xs" />}
    </button>
  );
}
